// Single source of truth for all site-wide navigation, links, and configuration

export interface NavItem {
  label: string;
  path: string;
  children?: { label: string; path: string; external?: boolean }[];
}

// 7 top-level items. CAREERS and BEGINNERS deliberately live in the footer
// only (SERP sitelink strategy \u2014 see PR feat/serp-optimisation). If you edit
// labels/paths/order here, ALSO run scripts/sync-cms-nav.js: a saved Firestore
// websiteContent/gridsAndCards.navItems doc overrides these code defaults.
export const NAV_ITEMS: NavItem[] = [
  { label: 'HOME', path: '/' },
  { label: 'PROPERTY DEALS', path: '/best-property-investment-opportunities' },
  {
    label: 'SERVICES',
    path: '/services',
    children: [
      { label: 'Millionaire Strategy', path: '/millionaire-strategy' },
      { label: 'Group Millionaire Strategy', path: '/family-pty' },
      { label: 'Home Loans', path: '/home-loans' },
      { label: 'Re-sell a Property', path: '/resales' },
      { label: 'Find a Tenant', path: '/rentals' },
      { label: 'Independent Trustee', path: '/independent-trustee' },
      { label: 'Insurance', path: '/insurance' },
      { label: 'Trust Creation', path: '/trust-creation' },
      { label: 'Register a Company (PTY)', path: '/company-registration' },
      { label: 'Will and Testaments', path: '/wills' },
      { label: 'Capital Investment', path: '/capital-investment' },
    ],
  },
  {
    label: 'TOOLS',
    path: '/tools/calculator',
    children: [
      { label: 'Millionaire Strategy', path: '/millionaire-strategy' },
      { label: 'Bond Calculator', path: '/tools/calculator' },
      { label: 'Affordability Calculator', path: '/tools/affordability' },
      { label: 'Tax Savings Calculator', path: '/tools/tax-savings' },
      { label: 'Free Credit Check', path: '/tools/credit-check' },
      { label: 'PropInvest\u2122 Free App', path: '/mobile-app' },
    ],
  },
  { label: 'EDUCATION', path: '/education' },
  { label: 'INSIGHTS', path: '/blog' },
  { label: 'CAREERS', path: '/careers' },
  { label: 'CONTACT', path: '/contactus' },
];

export const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/propertyinvestmadeeasy',
  instagram: 'https://www.instagram.com/propinvest.invest/',
  twitter: 'https://x.com/PropInvest5',
  linkedin: 'https://www.linkedin.com/company/18474920/admin/page-posts/published/',
  youtube: 'https://www.youtube.com/channel/UC9HgDzcOBrmy9Vr_cs6Qgcw',
  whatsapp: 'https://api.whatsapp.com/send?phone=27215183352',
} as const;

export const CONTACT_INFO = {
  phone: '021 872 0028',
  phoneLink: 'tel:+27218720028',
  whatsappPhone: '021 518 3352',
  whatsappLink: 'https://api.whatsapp.com/send?phone=27215183352',
  email: 'info@propinvest.co.za',
  emailLink: 'mailto:info@propinvest.co.za',
  careersEmail: 'careers@propinvest.co.za',
  careersEmailLink: 'mailto:careers@propinvest.co.za?subject=I%20would%20like%20to%20apply%20for%20a%20position%20at%20PropInvest',
  address: 'Paarl, Western Cape - South Africa',
  phoneFormatted: '+27 (0) 21 872 0028',
} as const;

export const APP_LINKS = {
  mobileApp: '/mobile-app',
  appStore: 'https://apps.apple.com/us/app/propinvest/id6744142867',
  googlePlay: 'https://play.google.com/store/apps/details?id=com.propinvest.app&pli=1',
} as const;

export const EXTERNAL_TOOLS = {
  trusts: 'https://trusts.propinvest.co.za/',
  pty: 'https://pty.propinvest.co.za/',
  lastWill: 'https://lastwill.propinvest.co.za/',
  prequalify: 'https://prequalify.propinvest.co.za/',
  webApp: 'https://propinvest-app.replit.app/',
} as const;

export const FOOTER_COLUMNS = {
  company: [
    { label: 'HOME', path: '/' },
    { label: 'PROPERTY INVESTMENT DEALS', path: '/best-property-investment-opportunities' },
    { label: 'EDUCATION', path: '/education' },
    { label: 'CAREERS', path: '/careers' },
    { label: 'CONTACT US', path: '/contactus' },
  ],
  freeTools: [
    { label: 'BOND CALCULATOR', path: '/tools/calculator' },
    { label: 'AFFORDABILITY CALCULATOR', path: '/tools/affordability' },
    { label: 'FREE CREDIT CHECK', path: '/tools/credit-check' },
    { label: 'TAX SAVINGS CALCULATOR', path: '/tools/tax-savings' },
    { label: 'CONSULTATION', path: '/consultation' },
    { label: 'MOBILE APP', path: '/mobile-app' },
  ],
  services: [
    { label: 'HOME LOANS', path: '/home-loans' },
    { label: 'RESALES', path: '/resales' },
    { label: 'RENTALS', path: '/rentals' },
    { label: 'INSURANCE', path: '/insurance' },
    { label: 'TRUSTEE', path: '/independent-trustee' },
    { label: 'GROUP MILLIONAIRE STRATEGY', path: '/family-pty' },
  ],
} as const;

export const BUSINESS_HOURS = {
  weekdays: '08:00 - 17:00',
  weekend: 'CLOSED',
  holidays: 'CLOSED',
} as const;

export interface HeroSlide {
  title: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
  bgColor: string; // Tailwind gradient or bg class
  bgImage?: string;  // Static image (jpg/png/gif/webp) URL
  bgVideo?: string;  // Video (mp4/webm) URL

  // Rich content blocks
  contentBlocks?: import('../types/websiteCms.types').CmsSlideContentBlock[];
  contentPosition?: 'left' | 'center' | 'right';
  contentVertical?: 'top' | 'center' | 'bottom';

  // Development CTA
  ctaDevelopmentId?: string;
  ctaDevelopmentSlug?: string;
  ctaDevelopmentName?: string;
  ctaStyle?: 'primary' | 'outline' | 'ghost';

  // Timing
  durationSeconds?: number;
}

export const HERO_SLIDES: HeroSlide[] = [
  {
    title: 'FIND YOUR PERFECT INVESTMENT',
    subtitle: 'Exclusive property opportunities across South Africa',
    ctaText: 'View Property Deals',
    ctaLink: '/best-property-investment-opportunities',
    bgColor: 'from-gray-800 to-gray-900',
  },
  {
    title: 'DOWNLOAD OUR FREE PROPINVEST APP',
    subtitle: 'Find investments, see what you can afford and ask Cindy, our AI assistant',
    ctaText: 'Download Now',
    ctaLink: APP_LINKS.mobileApp,
    bgColor: 'from-primary-dark to-primary',
  },
  {
    title: 'INSTANT FREE CREDIT SCORE',
    subtitle: 'Know where you stand before you invest',
    ctaText: 'Check Now',
    ctaLink: EXTERNAL_TOOLS.prequalify,
    bgColor: 'from-gray-700 to-gray-800',
  },
];

export const SERVICES_GRID = [
  { title: 'COMPANIES, TRUSTS AND TESTAMENTS', image: '/images/services/trusts.jpg' },
  { title: 'HOME LOANS, BONDS AND CREDIT SCORES', image: '/images/services/refinance.jpg' },
  { title: 'INSURANCE', image: '/images/services/insurance.jpg' },
  { title: 'PROPERTY RENTALS AND RESALES', image: '/images/services/rentals.jpg' },
  { title: 'SECTION 13 TAX SAVINGS', image: '/images/services/tax.jpg' },
  { title: 'GROUP MILLIONAIRE STRATEGY', image: '/images/services/group.jpg' },
] as const;

interface ServiceButton {
  label: string;
  path: string;
  external?: boolean;
  fullWidth?: boolean;
}

export const SERVICES_BUTTONS: ServiceButton[] = [
  { label: 'Trust Creation', path: '/trust-creation' },
  { label: 'Register a Company (PTY)', path: '/company-registration' },
  { label: 'Home Loans', path: '/home-loans' },
  { label: 'Independent Trustee', path: '/independent-trustee' },
  { label: 'Insurance', path: '/insurance' },
  { label: 'Re-sell a Property', path: '/resales' },
  { label: 'Will and Testaments', path: '/wills' },
  { label: 'Find a Tenant', path: '/rentals' },
  { label: 'Group Millionaire Strategy', path: '/family-pty' },
];

interface ToolItem {
  title: string;
  path: string;
  image: string;
  external?: boolean;
}

export const TOOLS_GRID: ToolItem[] = [
  { title: 'Home Loan Calculator', path: '/tools/calculator', image: '' },
  { title: 'Affordability Calculator', path: '/tools/affordability', image: '' },
  { title: 'Tax Savings Calculator', path: '/tools/tax-savings', image: '' },
  { title: 'Free Credit Score', path: EXTERNAL_TOOLS.prequalify, image: '', external: true },
  { title: 'Bond Pre-approval', path: EXTERNAL_TOOLS.prequalify, image: '', external: true },
  { title: 'PropInvest\u2122 Free App', path: '/mobile-app', image: '' },
];

export const BEGINNERS_VIDEOS = [
  { title: 'THE ULTIMATE PROPERTY BEGINNERS GUIDE SERIES', videoId: '' },
  { title: '1. WHY INVEST IN PROPERTY', videoId: '' },
  { title: '2. HOW TO INVEST IN PROPERTY', videoId: '' },
  { title: '3. PROPINVEST\u2122 SERVICES', videoId: '' },
  { title: '4. PROPERTY TOOLS YOU NEED', videoId: '' },
  { title: '5. PROPERTY CONSULTATION', videoId: '' },
] as const;

export const REGION_CARDS = [
  { name: 'WESTERN CAPE', price: 'R945 000', image: '/images/regions/western-cape.jpg' },
  { name: 'KWAZULU-NATAL', price: 'R1 429 000', image: '/images/regions/kzn.jpg' },
  { name: 'GAUTENG', price: 'R650 000', image: '/images/regions/gauteng.jpg' },
  { name: 'NORTH WEST', price: 'R850 000', image: '/images/regions/north-west.jpg' },
] as const;

interface CTACard {
  title: string;
  subtitle: string;
  link: string;
  image: string;
}

export const CTA_CARDS: CTACard[] = [
  {
    title: 'WHY PROPERTY INVESTMENT?',
    subtitle: 'Where do I start?',
    link: '/education',
    image: '/images/cta/why-property.jpg',
  },
  {
    title: 'SPEAK TO AN EXPERT',
    subtitle: 'Free Online Consultation',
    link: '/consultation',
    image: '/images/cta/consultation.jpg',
  },
  {
    title: 'FREE TOOLS',
    subtitle: 'Everything Under One Roof!',
    link: '/tools/calculator',
    image: '/images/cta/tools.jpg',
  },
  {
    title: 'WHY CHOOSE PROPINVEST\u2122?',
    subtitle: 'What makes PropInvest\u2122 different?',
    link: '/',
    image: '/images/cta/why-propinvest.jpg',
  },
];
